import React, { useState, useEffect, useMemo } from "react";
import { useApi } from "../../../hooks/useApi";

const TarjetaRangoDinamica = ({ campo, valor, onChange }) => {
  const config = campo.opciones || {}; 
  let estado = null;
  if (valor !== "" && valor !== undefined) {
    const num = parseFloat(valor);
    if (!isNaN(num)) {
      estado = true;
      if (config.min !== undefined && num < config.min) estado = false;
      if (config.max !== undefined && num > config.max) estado = false;
    }
  }
  
  return (
    <div className={`p-4 rounded-2xl border transition-all shadow-sm ${estado === true ? 'bg-emerald-50/80 border-emerald-200' : estado === false ? 'bg-rose-50/80 border-rose-300' : 'bg-white/60 border-slate-200'}`}>
      <div className="flex justify-between items-start mb-3">
        <div className="flex gap-2 items-center">
          <span className="text-2xl">{config.icono || "🏷️"}</span>
          <div>
            <h4 className="text-xs font-black text-slate-800 uppercase leading-tight">{campo.etiqueta}</h4>
            <p className="text-[9px] font-bold text-slate-500 mt-0.5">{config.rangoTexto}</p>
          </div>
        </div>
      </div>
      <div className="relative">
        <input 
          type="number" step="0.1" 
          value={valor || ""} 
          onChange={(e) => onChange(campo.nombre_campo, e.target.value)} 
          required={campo.requerido === 1}
          className={`w-full h-[40px] rounded-xl px-4 text-sm font-black shadow-inner border transition-all ${estado === true ? 'bg-emerald-100 text-emerald-800 border-emerald-300 focus:border-emerald-500' : estado === false ? 'bg-rose-100 text-rose-800 border-rose-400 focus:border-rose-500' : 'bg-white text-slate-800 border-slate-300 focus:border-cyan-400'}`} 
        />
        {config.min !== undefined && <span className="absolute right-4 top-2.5 text-xs font-black text-slate-400">°C</span>}
      </div>
      {estado === false && <p className="text-[10px] text-rose-600 font-bold mt-2 leading-tight">⚠️ Fuera del rango permitido.</p>}
    </div>
  );
};

export default function FormularioDinamico({ slug, usuario, onGuardadoExitoso, onCancelar }) {
  const { request } = useApi();
  const [formulario, setFormulario] = useState(null);
  const [respuestas, setRespuestas] = useState({});
  const [loading, setLoading] = useState(true);
  const [enviando, setEnviando] = useState(false);
  
  const [sedes, setSedes] = useState([]);
  const [empleadosDB, setEmpleadosDB] = useState([]);
  const [buscarTodasSedes, setBuscarTodasSedes] = useState(false);

  useEffect(() => {
    const cargarDatos = async () => {
      setLoading(true);
      try {
        console.log("🔍 [DEBUG] Iniciando carga para el slug:", slug);
        
        // 1. Cargar Estructura
        const response = await request(`/formularios-dinamicos/${slug}`);
        console.log("📦 [DEBUG] Respuesta estructura del formulario:", response);
        
        // 2. Cargar Sedes (con fallback robusto)
        let sedesCargadas = [];
        const resSedes = await request(`/sedes`);
        console.log("🏢 [DEBUG] Respuesta sedes (hook useApi):", resSedes);
        
        if (resSedes?.success && Array.isArray(resSedes.sedes)) {
          sedesCargadas = resSedes.sedes;
        } else {
          const apiUrl = process.env.REACT_APP_API_URL;
          const fb = await fetch(`${apiUrl}/api/sedes`);
          const fbData = await fb.json();
          console.log("🏢 [DEBUG] Respuesta sedes (fallback fetch):", fbData);
          if (fbData.success) sedesCargadas = fbData.sedes;
        }
        setSedes(sedesCargadas);

        // 3. Cargar Empleados
        const token = localStorage.getItem("token");
        const apiUrlEmp = process.env.REACT_APP_API_URL;
        const resEmp = await fetch(`${apiUrlEmp}/api/empleados`, {
           headers: { ...(token && { Authorization: `Bearer ${token}` }) }
        });
        const dataEmp = await resEmp.json();
        console.log("👥 [DEBUG] Empleados cargados:", dataEmp);
        setEmpleadosDB(Array.isArray(dataEmp) ? dataEmp : (dataEmp.data || []));

        // 4. Inicializar Estado
        if (response?.success && response.data) {
          let datosForm = Array.isArray(response.data) ? response.data[0] : response.data;
          if (datosForm.data) datosForm = Array.isArray(datosForm.data) ? datosForm.data[0] : datosForm.data;
          
          setFormulario(datosForm);
          setRespuestas({ fechaEvento: new Date().toISOString().split('T')[0] });
        } else {
          throw new Error(response?.message || "La estructura del formulario vino vacía o falló en el servidor.");
        }
      } catch (err) {
        console.error("❌ [ERROR CRÍTICO] Error al cargar datos del formulario:", err);
        alert(`⚠️ Error al cargar el formulario (${slug}):\n${err.message || JSON.stringify(err)}`);
      } finally {
        setLoading(false);
      }
    };
    if (slug) cargarDatos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const handleChange = (nombre, valor) => {
    setRespuestas(prev => {
      const next = { ...prev, [nombre]: valor };
      if (nombre === 'pdv') next.verificador = ""; 
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setEnviando(true);
    const payload = {
      usuario_documento: usuario?.documento || "SIN_DOC",
      datos_json: respuestas,
      observaciones_generales: respuestas.observaciones || ""
    };
    const res = await request(`/formularios-dinamicos/${slug}/respuestas`, "POST", payload);
    if (res?.success) onGuardadoExitoso && onGuardadoExitoso(res);
    else alert("Error al guardar.");
    setEnviando(false);
  };

  const listaParaMostrar = useMemo(() => {
    return empleadosDB.filter(emp => {
      const estaActivo = !emp.empl_fecret || String(emp.empl_fecret).toUpperCase() === 'NULL' || emp.empl_fecret === '0000-00-00';
      if (!estaActivo) return false;
      if (buscarTodasSedes) return true;
      if (!emp.area_nombre || !respuestas.pdv) return false;
      return emp.area_nombre.toUpperCase() === (respuestas.pdv || "").toUpperCase();
    });
  }, [empleadosDB, respuestas.pdv, buscarTodasSedes]);

  const secciones = useMemo(() => {
    if (!formulario?.campos) return {};
    return formulario.campos.reduce((acc, campo) => {
      const sec = campo.seccion || "Otras";
      if (!acc[sec]) acc[sec] = [];
      acc[sec].push(campo);
      return acc;
    }, {});
  }, [formulario]);

  const renderCampo = (campo) => {
    // Definir si ocupa 1 columna o 3 (ancho completo en la tarjeta superior)
    const colSpan = campo.tipo_componente === 'verificador_sede' || campo.tipo_componente === 'textarea' ? 'md:col-span-3' : 'col-span-1';

    switch (campo.tipo_componente) {
      case "tarjeta_rango":
        return <TarjetaRangoDinamica key={campo.id} campo={campo} valor={respuestas[campo.nombre_campo]} onChange={handleChange} />;
      
      case "select_sede":
        return (
          <div key={campo.id} className={colSpan}>
            <label className="block text-[10px] font-black text-slate-700 mb-1.5 uppercase">{campo.etiqueta} *</label>
            <select required={campo.requerido === 1} value={respuestas[campo.nombre_campo] || ""} onChange={(e) => handleChange(campo.nombre_campo, e.target.value)} className="w-full h-[40px] bg-white border border-slate-300 focus:border-cyan-400 rounded-xl px-4 text-xs font-bold text-slate-800 cursor-pointer shadow-sm text-center">
              <option value="" disabled>-- Seleccione una sede --</option>
              {sedes.map(s => <option key={s.id} value={s.nombre.toUpperCase()}>{s.nombre} {s.codigo_bodega ? `(Bodega: ${s.codigo_bodega})` : ""}</option>)}
            </select>
          </div>
        );

      case "verificador_sede":
        return (
          <div key={campo.id} className={`${colSpan} pt-2 border-t border-slate-200/60 mt-2`}>
            <label className="block text-[10px] font-black text-slate-700 mb-1.5 uppercase">{campo.etiqueta} *</label>
            <input 
              type="text" 
              list={`lista_${campo.nombre_campo}`} 
              value={respuestas[campo.nombre_campo] || ""} 
              onChange={(e) => handleChange(campo.nombre_campo, e.target.value)} 
              placeholder={!respuestas.pdv ? "Elige sede primero" : "Escribe o elige nombre..."} 
              disabled={!respuestas.pdv} 
              required={campo.requerido === 1} 
              className="w-full h-[40px] bg-white border border-slate-300 focus:border-cyan-400 rounded-xl px-4 text-xs font-bold text-slate-800 shadow-sm disabled:opacity-50" 
              autoComplete="off" 
            />
            <datalist id={`lista_${campo.nombre_campo}`}>
              {listaParaMostrar.map((emp, i) => <option key={i} value={emp.terc_nom}>{buscarTodasSedes ? `${emp.empl_cargo || "Sin Cargo"} (${emp.area_nombre || "Sede"})` : (emp.empl_cargo || "Sin Cargo")}</option>)}
            </datalist>
            <div className="mt-2 flex items-center justify-end">
              <input type="checkbox" id="buscarGlobalV" checked={buscarTodasSedes} onChange={(e) => setBuscarTodasSedes(e.target.checked)} className="mr-1.5 cursor-pointer accent-cyan-600" disabled={!respuestas.pdv} />
              <label htmlFor="buscarGlobalV" className="text-[9px] text-cyan-700 font-black cursor-pointer uppercase">Buscar en todas las sedes</label>
            </div>
          </div>
        );

      case "select":
        const opts = Array.isArray(campo.opciones) ? campo.opciones : [];
        return (
          <div key={campo.id} className={colSpan}>
            <label className="block text-[10px] font-black text-slate-700 mb-1.5 uppercase">{campo.etiqueta} *</label>
            <select required={campo.requerido === 1} value={respuestas[campo.nombre_campo] || ""} onChange={(e) => handleChange(campo.nombre_campo, e.target.value)} className="w-full h-[40px] bg-white border border-slate-300 focus:border-cyan-400 rounded-xl px-4 text-xs font-bold text-slate-800 shadow-sm text-center">
              <option value="" disabled>Seleccione...</option>
              {opts.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
        );
        
      case "textarea":
        return (
          <div key={campo.id} className={colSpan}>
            <label className="block text-[10px] font-black text-slate-700 mb-2 uppercase tracking-widest">{campo.etiqueta}</label>
            <textarea required={campo.requerido === 1} value={respuestas[campo.nombre_campo] || ""} onChange={(e) => handleChange(campo.nombre_campo, e.target.value)} className="w-full h-[80px] bg-white/90 border border-slate-200 focus:border-cyan-400 rounded-xl px-4 py-3 text-xs font-medium text-slate-800 shadow-sm resize-none" placeholder="Escriba aquí..."/>
          </div>
        );
        
      default:
        return (
          <div key={campo.id} className={colSpan}>
            <label className="block text-[10px] font-black text-slate-700 mb-1.5 uppercase">{campo.etiqueta} *</label>
            <input type={campo.tipo_componente} required={campo.requerido === 1} value={respuestas[campo.nombre_campo] || ""} onChange={(e) => handleChange(campo.nombre_campo, e.target.value)} className="w-full h-[40px] bg-white border border-slate-300 focus:border-cyan-400 rounded-xl px-4 text-xs font-bold text-slate-800 shadow-sm block text-center" style={{ margin: 0, padding: campo.tipo_componente==='date' ? '0 1rem' : undefined, lineHeight: campo.tipo_componente==='date' ? '40px' : undefined }}/>
          </div>
        );
    }
  };

  if (loading || !formulario) return <div className="p-10 text-center text-white font-bold">Cargando formato...</div>;

  return (
    <div className="bg-white/20 backdrop-blur-3xl rounded-[34px] shadow-2xl max-w-5xl w-full mx-auto border border-white/30 animate-fade-in relative h-[90vh] overflow-hidden flex flex-col">
      {/* CABECERA IDÉNTICA AL ORIGINAL */}
      <div className="bg-gradient-to-r from-cyan-600/90 to-blue-800/90 border-b border-white/20 px-6 py-4 z-40 flex items-center justify-between shadow-md shrink-0">
        <div className="flex items-center">
          <button type="button" onClick={onCancelar} className="mr-4 w-10 h-10 flex-none bg-white/20 hover:bg-white/40 text-white flex items-center justify-center rounded-full transition-all border border-white/40 shadow-sm cursor-pointer">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12"></path></svg>
          </button>
          <div>
            <h1 className="text-lg sm:text-xl font-black text-white leading-tight">{formulario.titulo}</h1>
            <span className="bg-white/20 text-white border border-white/30 text-[10px] font-bold px-2 py-0.5 rounded-md mt-1 inline-block">CÓDIGO: {formulario.codigo_modulo} | Versión: {formulario.version || '03'}</span>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-4 sm:p-8 space-y-6 custom-scrollbar">
        
        {/* SECCIÓN GENERAL (3 Columnas) */}
        {secciones['General'] && (
          <div className="bg-white/60 backdrop-blur-xl p-6 rounded-[24px] shadow-sm border border-white/80 grid grid-cols-1 md:grid-cols-3 gap-4">
            {secciones['General'].map(renderCampo)}
          </div>
        )}

        {/* RESTO DE SECCIONES (Grilla Dinámica de Tarjetas) */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {Object.entries(secciones).filter(([sec]) => sec !== 'General' && sec !== 'Observaciones').map(([nombreSeccion, campos]) => {
             // Asignamos colores a las tarjetas basándonos en el nombre de la sección como en el diseño original
             let colorHeader = "text-slate-800 border-slate-300";
             let iconHeader = "📋";
             if(nombreSeccion.includes("Refrigeración")) { colorHeader = "text-cyan-800 border-cyan-200"; iconHeader = "❄️"; }
             if(nombreSeccion.includes("Congelación")) { colorHeader = "text-blue-800 border-blue-200"; iconHeader = "🧊"; }
             if(nombreSeccion.includes("Caliente")) { colorHeader = "text-orange-800 border-orange-200"; iconHeader = "🔥"; }

             return (
              <div key={nombreSeccion} className="bg-white/60 backdrop-blur-xl p-6 rounded-[24px] shadow-sm border border-white/80">
                <h3 className={`text-sm font-black uppercase tracking-widest mb-4 flex items-center gap-2 border-b pb-2 ${colorHeader}`}>
                  <span>{iconHeader}</span> {nombreSeccion}
                </h3>
                <div className="space-y-4">
                  {campos.map(renderCampo)}
                </div>
              </div>
            );
          })}
        </div>

        {/* SECCIÓN OBSERVACIONES Y BOTÓN GUARDAR */}
        <div className="bg-white/60 backdrop-blur-xl p-6 rounded-[24px] shadow-sm border border-white/80">
           {secciones['Observaciones'] && secciones['Observaciones'].map(renderCampo)}
           <button type="submit" disabled={enviando} className="w-full mt-6 bg-gradient-to-r from-cyan-600 to-blue-700 hover:from-cyan-500 hover:to-blue-600 text-white font-black py-4 rounded-xl shadow-lg active:scale-95 transition-all text-[11px] tracking-widest uppercase cursor-pointer border border-cyan-400">
             {enviando ? 'Guardando...' : 'Guardar Lecturas'}
           </button>
        </div>
      </form>
    </div>
  );
}